import Link from "next/link";
import Conversation from "@/base/components/Conversation";
import React, {useEffect, useState} from "react";
import {SupportTicket} from "@/base/types/SupportTicket";
import {useTranslation} from "next-i18next";
import {ModifyURL} from "@/base/utils/fetch";
import {getCache, putCache} from "@/base/utils/cache";
import ReCaptchaParent, {useCaptcha} from "@/base/utils/recaptcha";
import {useUser} from "@/base/components/auth/UserProvider";
import {useNotification} from "@/base/components/NotificationProvider";
import SimpleCard from "@/base/components/dashboard/SimpleCart";
import {Button, Input, InputRef} from "antd";
import {getRefValue} from "@/base/utils/form";
import {RateObject} from "@/base/types/RateObject";
import {default as NextImage} from "next/image";
import LikeOutlined from "@ant-design/icons/LikeOutlined";
import MessageOutlined from "@ant-design/icons/MessageOutlined";
import StarFilled from "@ant-design/icons/StarFilled";
import {CurrentEmergency} from "@/base/components/alerts/CurrentEmergency";
import SupportWorkingHours from "@/base/components/support/SupportWorkingHours";
import useMemoryState from "@/base/utils/useMemoryState";
import SupportStatus from "@/base/types/SupportStatus";
import {useStaticData} from "@/base/utils/statisdata";
import WarningFilled from "@ant-design/icons/WarningFilled";
import gtag from "@/base/components/util/gtag";
import {useRouter} from "next/router";
import {PublicUser} from "@/base/types/PublicUser";
import {ConversationMessage} from "@/base/types/ConversationMessage";
import {usePusher} from "@/base/utils/pusher.provider";

export type SupportChatProps = {
	className?: string;
	children?: React.ReactNode;
	supportTicket?: SupportTicket;
	maxHeight?: number;
	beforeChildren?: React.ReactNode;
	loading?: boolean;
	initialText?: string;
	onChangeTicket?: (ticket?: SupportTicket) => Promise<boolean | void>;
}

export const LeaveRate = ({rate, onChange, value}: {
	rate: RateObject,
	onChange?: (number: number) => void,
	value?: number
}) => {
	const maxStars = 5;

	const [currentStar, setCurrentStar] = useState<number>(0);
	const [selectedStar, setSelectedStar] = useState<number>(0);

	useEffect(() => {
		if (value)
		{
			setSelectedStar(value);
		}
	}, [value]);

	useEffect(() => {
		if (onChange)
		{
			onChange(selectedStar);
		}
	}, [selectedStar]);

	const stars = [];
	for (let i = 0; i < maxStars; i++)
	{
		const isHover = currentStar > 0 && i < currentStar;
		const isSelected = selectedStar > 0 && i < selectedStar;
		stars.push(<div className={`px-0.5 cursor-pointer`} onMouseOver={() => setCurrentStar(i + 1)} onMouseOut={() => setCurrentStar(0)} onClick={() => setSelectedStar(i + 1)}>
			<StarFilled
				key={i}
				className={`duration-150 text-2xl ${isSelected && i == selectedStar - 1 && `!drop-shadow-lg`} ${isSelected ? (isHover ? '!text-primary/50' : `!text-primary`) : (isHover ? '!text-primary-500' : '!text-gray-300')}`}
			/>
		</div>)
	}

	return <div
		className={`flex flex-col gap-4 justify-center items-center  w-fit px-4  rounded-xl relative bg-base-bg`}
	>
		<NextImage src={rate.image_url || '/image/logo.svg'} unoptimized width={48} height={48} alt={rate.name || 'StableProxy'} className={`overflow-hidden rounded-xl aspect-square absolute top-4 left-1/2 -translate-x-1/2 scale-150 blur-md opacity-20 -z-1`}/>
		<NextImage src={rate.image_url || '/image/logo.svg'} unoptimized width={48} height={48} alt={rate.name || 'StableProxy'} className={`overflow-hidden rounded-xl aspect-square relative shadow`}/>
		<div className={`flex flex-col gap-1`}>
			<p className={`text-md font-medium my-0`}>{rate.name || 'StableProxy user'}</p>
			{rate.role && <p className={`text-sm text-text-2/50 my-0`}>{rate.role}</p>}
		</div>

		{stars.length > 0 && <div className={`flex`}>
			{stars}
		</div>}
	</div>
}

export const SupportChatInside = ({
	className = '',
	initialText = '',
	supportTicket,
	onChangeTicket,
	maxHeight,
	loading = false,
	children,
	beforeChildren
}: SupportChatProps) => {
	const {t} = useTranslation();
	const {executeRecaptcha} = useCaptcha();
	const UserContext = useUser();
	const router = useRouter()
	const [api] = useNotification();
	const {pusher, isUserConnected, isConnected, onEvent} = usePusher();

	const [loadingTicket, setLoadingTicket] = useState<boolean>(false);
	const [supportTicketState, setSupportTicketState] = useState<SupportTicket | undefined>(supportTicket);
	const [supportRateList, setSupportRateList] = useState<RateObject[]>([]);
	const [requestContactInfo, setRequestContactInfo] = useState<boolean>(false);
	const [requestContactInfoAnimation, setRequestContactInfoAnimation] = useState<boolean>(false);
	const [allowSendContactInfo, setAllowSendContactInfo] = useState<boolean>(false);
	const contactNameRef = React.useRef<InputRef>(null);
	const contactEmailRef = React.useRef<InputRef>(null);
	const [previousForm, setPreviousForm] = useState<FormData | undefined>(undefined);
	const [ratedSubjects, setRatedSubjects] = useState<Record<number, number>>({});
	const reviewTextRef = React.useRef<InputRef>(null);
	const [allowedSendReview, setAllowedSendReview] = useState<boolean>(false);
	const [alreadyRated, setAlreadyRated] = useState<boolean>(false);

	const checkAbilityToSendReview = () => {
		if (getRefValue(reviewTextRef)?.length > 0 || Object.keys(ratedSubjects).length > 0)
		{
			setAllowedSendReview(true);
		}
		else
		{
			setAllowedSendReview(false);
		}
	}

	const onChangeContactInfo = () => {
		if (getRefValue(contactNameRef) && getRefValue(contactEmailRef))
		{
			setAllowSendContactInfo(true);
		}
		else
		{
			setAllowSendContactInfo(false);
		}
	}

	const sendRate = async () => {
		if (!allowedSendReview)
		{
			return;
		}

		setLoadingTicket(true);

		return UserContext.Fetch({
			url: ModifyURL(`/support/rate/${supportTicketState?.id}`),
			method: 'PUT',
			body: {
				comment: getRefValue(reviewTextRef),
				rates: ratedSubjects
			}
		}).then((res: {
			data: {
				status: string,
			}
		}) => {
			if (!res.data || !res.data.status || res.data.status !== 'success')
			{
				throw new Error('Invalid response');
			}

			setAlreadyRated(true);
		}).catch(err => {
			api.error({
				message: err.message || err
			});
		}).finally(() => setLoadingTicket(false));
	}

	const changeTicket = async (ticket?: SupportTicket) => {
		if (!onChangeTicket || (onChangeTicket && !(await onChangeTicket(ticket))))
		{
			console.info('Change ticket', ticket);

			setSupportRateList([]);
			setRatedSubjects({});
			setAlreadyRated(false);
			if (ticket?.is_closed)
			{
				setLoadingTicket(true);
				await UserContext.Fetch({
					url: ModifyURL(`/support/rate/${ticket.id}`),
					method: 'GET'
				}).then((res: {
					data: {
						already_rated: boolean,
						subjects: RateObject[]
					}
				}) => {
					if (res.data && res.data.already_rated)
					{
						setAlreadyRated(true);
					}
					else if (res.data && res.data.subjects)
					{
						setSupportRateList(res.data.subjects);
					}
				}).catch(err => {
					api.error({
						message: err.message || err
					});
				}).finally(() => setLoadingTicket(false));
			}

			return setSupportTicketState(ticket);
		}
	}

	useEffect(() => {
		if (!isConnected() || !supportTicketState)
		{
			return;
		}

		return onEvent(`private-conversation-${supportTicketState?.id}-SUPPORT`, `refresh`, (data: {
			come_from: PublicUser,
			message: ConversationMessage
		}) => {
			console.info('Force refresh', data);

			UserContext.Fetch({
				url: ModifyURL(`/support/tickets/${supportTicketState?.id}`)
			}).then(res => changeTicket(res?.data)).catch(err => {
				console.error(err);
			});
		});
	}, [pusher, UserContext.user, supportTicketState]);

	const onNewTicket = async (form?: FormData) => {
		if (!form)
		{
			return;
		}

		if (!UserContext.user && !requestContactInfo)
		{
			setPreviousForm(form);
			setRequestContactInfo(true);
			setTimeout(() => setRequestContactInfoAnimation(true), 100);
			return;
		}

		setLoadingTicket(true);

		const captchaValue = await executeRecaptcha('support');

		form.append('g-recaptcha-response', captchaValue);

		const name = getRefValue(contactNameRef);
		const email = getRefValue(contactEmailRef);
		if (requestContactInfo)
		{
			form.append('name', name);
			form.append('email', email);
		}

		return UserContext.Fetch({
			url: ModifyURL(`/support/tickets`),
			method: 'POST',
			body: form,
		}).then(async (res: {
			data: {
				ticket: SupportTicket
			}
		}) => {
			if (!res.data || !res.data.ticket)
			{
				throw new Error('Invalid response');
			}

			if (requestContactInfo)
			{
				gtag("event", "start_support_new", {
					'event_category': 'chat_beginning',
					'Ваше ім': name,
					'Ваша адреса єл. пошти': email
				});
			}

			if (requestContactInfo)
			{
				setRequestContactInfo(false);
				setRequestContactInfoAnimation(false);
				setAllowSendContactInfo(false);
				setPreviousForm(undefined);
			}

			try
			{
				const cache: SupportTicket[] = [
					res.data.ticket,
					...(await getCache('support_tickets', []) || [])
				];

				await putCache('support_tickets', cache)
			}
			catch (e)
			{
				console.error(e);
			}

			return changeTicket(res.data.ticket);
		}).catch(err => {
			api.error({
				message: err.message || err
			});
		}).finally(() => setLoadingTicket(false));
	}
	// TODO: make pusher close event

	useEffect(() => {
		changeTicket(supportTicket);
	}, [supportTicket]);

	const CriticalNotify = null;


	const StaticData = useStaticData();

	var [status, setStatus] = useMemoryState<SupportStatus>({
		disabled: false,
		message: undefined
	});

	useEffect(() => {
		StaticData.getSupportStatus().then(status => {
			setStatus(status);
		})
	}, [router.locale]);

	const Alert = ({status, children, className}: { status: SupportStatus, children?: React.ReactNode, className?: string }) => {
		return <div className={`bg-red-300/20 w-fit !text-start overflow-hidden flex flex-col rounded-lg relative ${className}`}>
			<div className={`flex items-center gap-4 bg-amber-400/30 py-4 px-6 relative`}>
				<WarningFilled className={`!text-text-2 text-2xl`}/>
				<p className={`text-lg font-semibold text-text-2`}>
					{status.message?.title || ''}
				</p>
			</div>
			<div className={`py-4 px-6 relative z-[1]`}>
				<p className={`!mt-0`}>
					{status.message?.description || ''}
				</p>

				{children}
			</div>
		</div>
	}

	return <div className={`flex flex-col support-chat relative ${className}`}>
		{supportTicketState ? <>
			<Conversation
				conversationId={supportTicketState.id}
				type={'support'}
				className={`h-full flex flex-col`}
				canType={!supportTicketState.is_closed}
				maxHeight={maxHeight}
				loading={loadingTicket || loading}
				beforeChildren={beforeChildren}
				lastMessageChildren={<>
					{CriticalNotify}
					<SupportWorkingHours className={`mx-auto !my-4 !max-w-[560px] sp-support-work-hours`}/>
					{status.message && <Alert status={status} className={`mx-auto !my-4 !max-w-[560px] sp-support-work-hours`}/>}
					{(supportRateList.length > 0 || alreadyRated) && <div className={`flex flex-col gap-6`}>
						{alreadyRated ? <div className={`bg-base-bg w-fit gap-6 mx-auto rounded-xl shadow p-4 flex flex-col items-center flex-wrap justify-center text-start max-w-[600px]`}>
							<div className={`flex gap-4 items-center`}>
								<div>
									<LikeOutlined className={`text-4xl !text-green-500`}/>
								</div>
								<div>
									<p className={`text-md mb-2 font-medium font-montserrat-alternates text-text-2`}>{t('base-support:give-feedback-given.title')}</p>
									<p className={`mb-2`}>{t('base-support:give-feedback-given.description')}</p>
									<p className={``}>{t('base-support:give-feedback-given.description-2')}</p>
									<Link href={`/reviews`}>
										<Button
											type={'primary'}
											className={`w-fit mt-4 !shadow-none !bg-emerald-500 hover:!bg-emerald-4 00`}
										>
											{t('base-support:give-feedback-given.see-reviews')}
										</Button>
									</Link>
								</div>
							</div>
						</div> : <>
							<div className={`bg-base-bg w-fit gap-6 mx-auto rounded-xl shadow p-4 flex flex-col items-center flex-wrap justify-center text-start max-w-[600px]`}>
								<div className={`flex gap-4 items-center`}>
									<div>
										<MessageOutlined className={`text-4xl !text-amber-500`}/>
									</div>
									<div>
										<p className={`text-md mb-2 font-medium font-montserrat-alternates text-text-2`}>{t('base-support:give-feedback.title')}</p>
										<p className={``}>{t('base-support:give-feedback.description')}</p>
									</div>
								</div>
							</div>
							<div className={`bg-base-bg w-fit gap-6 mx-auto rounded-xl shadow p-4 flex flex-col items-center flex-wrap justify-center text-center`}>
								<div>
									<p className={`text-2xl font-semibold font-montserrat-alternates text-text-2`}>{t('base-support:give-review.title')}</p>
								</div>
								<div className={`flex items-center flex-wrap justify-center`}>
									{supportRateList.map((rate, index) => <LeaveRate rate={rate} key={index} onChange={(value) => {
										const n = {
											...ratedSubjects,
											[index]: value
										};

										Object.entries(n).forEach(([k, v]) => {
											if (v <= 0)
											{
												delete n[k as any];
											}
										});

										setRatedSubjects(n);
										checkAbilityToSendReview();
									}}/>)}
								</div>
								<div className={`w-full flex flex-col items-center gap-2.5`}>
									<Input.TextArea
										placeholder={t('base-support:give-review.comment.placeholder')}
										bordered={false}
										className={`w-full sp-input-clear !bg-primary-50 placeholder:whitespace-pre-wrap`}
										ref={reviewTextRef}
										onChange={checkAbilityToSendReview}
									/>
									<Button
										type={'primary'}
										className={`w-fit mt-4 ${!allowedSendReview && `opacity-30 pointer-events-none grayscale`}`}
										onClick={sendRate}
									>
										{t('base-support:give-review.submit')}
									</Button>
								</div>
							</div>
						</>}
					</div>}
				</>}
			>
				{children}
			</Conversation>
		</> : <>
			<Conversation
				conversationId={null}
				type={'support'}
				className={`h-full flex flex-col`}
				canType={!status.disabled}
				maxHeight={maxHeight}
				initialText={initialText}
				onSend={onNewTicket}
				loading={loadingTicket || loading}
				beforeChildren={beforeChildren}
			>
				{UserContext.loggedIn && <CurrentEmergency wrap={children => <div className={``}>
					<div className="gap-5 !scale-[0.8] origin-top pt-4">
						{children}
					</div>
				</div>}/>}
				<div className={`h-full w-full flex items-center justify-center sp-conversation-root-body`}>
					<div className={`text-center space-y-2 max-w-[700px] px-4 lg:px-6 sp-conversation-body my-4`}>
						<div>
							<h3 className={`font-subtitle h3`}>
								{t('base-support:ticket-create.title-2')}
							</h3>
							<p className={``}>
								{t('base-support:ticket-create.description')}
							</p>
						</div>
						<SupportWorkingHours className={`mx-auto !my-4 !max-w-[560px]`}/>
						{status.message && <Alert status={status} className={`mx-auto !my-4 !max-w-[560px]`}/>}
						<ul className={`list-inside space-y-1 text-start`}>
							<li>
								{t('base-support:ticket-create.description-1')}
								<Link href={`/faq`}>
									<span className={` !font-bold`}> {t('base-support:ticket-create.description-1-2')} </span>
								</Link>
								{t('base-support:ticket-create.description-1-1')}
							</li>
							<li>
								{t('base-support:ticket-create.description-2')}
							</li>
							<li>
								{t('base-support:ticket-create.description-3')}
							</li>
						</ul>
					</div>
				</div>
				{children}
			</Conversation>
		</>}
		{requestContactInfo && <div
			className={`absolute top-0 left-0 w-full h-full z-[3] backdrop-blur bg-slate-400/20 rounded-xl p-4 opacity-0 duration-300 flex items-center justify-center`}
			style={{
				opacity: requestContactInfoAnimation ? 1 : 0
			}}
		>
			<SimpleCard
				headerClassName={`bg-primary-50`}
				className={`relative`}
				title={t(`base-support:anonymous-contact-info.title`)}
				bodyClassName={`space-y-4 bg-primary-100`}
			>
				<div className={`gap-4 flex flex-col`}>
					<div>
						<p className={`mb-0`}>
							{t(`base-support:anonymous-contact-info.name.description`)}
						</p>
						<Input
							placeholder={t('base-support:anonymous-contact-info.name.placeholder')}
							className={`sp-input-clear !mt-2.5`}
							ref={contactNameRef}
							onChange={onChangeContactInfo}
						/>
					</div>
					<div>
						<p className={`mb-0`}>
							{t(`base-support:anonymous-contact-info.email.description`)}
						</p>
						<Input
							placeholder={t('base-support:anonymous-contact-info.email.placeholder')}
							className={`sp-input-clear !mt-2.5`}
							ref={contactEmailRef}
							onChange={onChangeContactInfo}
						/>
					</div>
					<div className={`w-full flex flex-col items-center`}>
						<Button
							type={'primary'}
							className={`w-fit ${!allowSendContactInfo && `opacity-30 pointer-events-none grayscale`}`}
							onClick={() => onNewTicket(previousForm)}
						>
							{t(`base-support:anonymous-contact-info.submit`)}
						</Button>
					</div>
				</div>
			</SimpleCard>
		</div>}
	</div>
}


export const SupportChat = (props: SupportChatProps) => {
	return <ReCaptchaParent>
		<SupportChatInside {...props}/>
	</ReCaptchaParent>
}